import { useState } from 'react';
import { Upload, CheckCircle, Loader2, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';
import VerificacionCliente from '../components/VerificacionCliente';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DECLARACION_URL = "https://res.cloudinary.com/db8u0dhhs/raw/upload/v1769733530/kompa/templates/declaracion_jurada_retiro_formal.docx";

export default function FirmarDeclaracion() {
  const [expedienteVerificado, setExpedienteVerificado] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [exitoso, setExitoso] = useState(false);

  // Cuando el cliente es verificado
  const handleVerificado = (expediente) => {
    setExpedienteVerificado(expediente);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (15MB max)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('El archivo es demasiado grande. Máximo 15MB');
        return;
      }
      // Validar tipo
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Solo se permiten archivos PNG, JPG o PDF');
        return;
      }
      setArchivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!archivo) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('declaracion', archivo);

      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteVerificado.id}/firmar-declaracion`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        setExitoso(true);
        toast.success('Declaración jurada recibida exitosamente');
      } else {
        const error = await response.json();
        toast.error(parseApiError(error.detail, 'Error al subir la declaración'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al subir la declaración');
    } finally {
      setUploading(false);
    }
  };

  // Si no hay expediente verificado, mostrar pantalla de verificación
  if (!expedienteVerificado) {
    return (
      <VerificacionCliente
        onVerificado={handleVerificado}
        titulo="Declaración Jurada de Retiro"
        descripcion="Verifica tu identidad para continuar"
      />
    );
  }

  if (exitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          </div>

          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </div>

              <h1 className="text-3xl font-bold text-white mb-3">
                ¡Declaración Recibida!
              </h1>
              
              <p className="text-gray-400 text-lg mb-8">
                Hemos recibido tu declaración jurada firmada exitosamente
              </p>

              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-5 mb-8">
                <p className="text-green-300 text-sm">
                  ✓ Tu expediente está completo<br />
                  ✓ Recibirás la notificación de pago pronto<br />
                  ✓ Te enviamos un email de confirmación
                </p>
              </div>

              <Button
                onClick={() => window.close()}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                Cerrar Ventana
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Declaración Jurada de Retiro</h1>
          <p className="text-gray-400">Sube tu declaración firmada con huellas</p>
        </div>

        {/* Info del Expediente */}
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Expediente</p>
                <p className="text-xl font-bold text-white">{expedienteVerificado.numero_expediente}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Solicitante</p>
                <p className="text-lg text-white">{expedienteVerificado.solicitante?.nombre_completo}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instrucciones */}
        <Card className="bg-blue-900/20 border-blue-500/30 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-400" />
              Instrucciones Importantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="text-gray-300 text-sm space-y-2 list-decimal list-inside">
              <li>Descarga la declaración jurada en blanco haciendo clic en el botón de abajo</li>
              <li>Imprímela y completa todos los datos requeridos</li>
              <li><strong className="text-white">Firma el documento</strong></li>
              <li><strong className="text-white">Coloca las huellas de tus dos pulgares</strong> en los espacios indicados</li>
              <li>Escanea o toma una foto clara del documento completo</li>
              <li>Sube el archivo usando el formulario de abajo</li>
            </ol>

            <div className="mt-4">
              <a 
                href={DECLARACION_URL}
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
              >
                <FileText className="w-5 h-5" />
                Descargar Declaración Jurada
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Formulario de subida */}
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Subir Declaración Firmada</CardTitle>
            <CardDescription className="text-gray-400">
              Archivo en formato PNG, JPG o PDF (máximo 15MB)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Input de archivo */}
              <div>
                <label className="block text-gray-300 mb-3 font-medium">
                  Selecciona tu declaración firmada:
                </label>
                <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-yellow-500/50 transition-colors">
                  <input
                    type="file"
                    id="declaracion-file"
                    className="hidden"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <label htmlFor="declaracion-file" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-300 font-medium mb-2">
                      {archivo ? archivo.name : 'Haz clic para seleccionar archivo'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      PNG, JPG o PDF - Máximo 15MB
                    </p>
                  </label>
                </div>
                {archivo && (
                  <p className="text-green-400 text-sm mt-2">✓ Archivo seleccionado: {archivo.name}</p>
                )}
              </div>

              {/* Advertencia */}
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-300 text-sm font-semibold">
                  ⚠️ IMPORTANTE: Asegúrate de que el documento incluya tu firma y las huellas de tus dos pulgares.
                </p>
              </div>

              {/* Botón de submit */}
              <Button
                type="submit"
                disabled={uploading || !archivo}
                className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Enviar Declaración
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          ¿Necesitas ayuda? Escríbenos a <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">desactivate@kompa.global</a>
        </p>
      </div>
    </div>
  );
}
